import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Network, Plus, Loader2, Users, Building2, Mail, Phone } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function OrgChartPage() {
  const { isAdmin } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ email: '', first_name: '', last_name: '', department: '', position: '', phone: '' });

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/employees`);
      setEmployees(res.data);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.email || !form.first_name || !form.last_name || !form.department || !form.position) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/employees`, form);
      toast.success('Employé ajouté');
      setDialogOpen(false);
      setForm({ email: '', first_name: '', last_name: '', department: '', position: '', phone: '' });
      fetchEmployees();
    } catch (error) {
      toast.error('Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  const departments = [...new Set(employees.map(e => e.department).filter(Boolean))];
  const employeesByDept = departments.map(dept => ({
    name: dept,
    employees: employees.filter(e => e.department === dept)
  }));

  const statusColors = {
    active: 'bg-emerald-100 text-emerald-700',
    on_leave: 'bg-amber-100 text-amber-700',
    inactive: 'bg-slate-100 text-slate-700'
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="spinner" /></div>;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="orgchart-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Organigramme</h1>
          <p className="text-slate-500 mt-1">Structure de l'organisation</p>
        </div>
        {isAdmin && (
          <Button onClick={() => setDialogOpen(true)} className="bg-slate-900 hover:bg-slate-800">
            <Plus className="w-4 h-4 mr-2" />Ajouter un employé
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <Users className="w-6 h-6 text-blue-500 mb-2" />
            <p className="text-sm text-slate-500">Total</p>
            <p className="text-2xl font-bold">{employees.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <Building2 className="w-6 h-6 text-purple-500 mb-2" />
            <p className="text-sm text-slate-500">Départements</p>
            <p className="text-2xl font-bold">{departments.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center mb-2">
              <div className="w-3 h-3 rounded-full bg-emerald-500" />
            </div>
            <p className="text-sm text-slate-500">Actifs</p>
            <p className="text-2xl font-bold text-emerald-600">{employees.filter(e => e.status === 'active').length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center mb-2">
              <div className="w-3 h-3 rounded-full bg-amber-500" />
            </div>
            <p className="text-sm text-slate-500">En congé</p>
            <p className="text-2xl font-bold text-amber-600">{employees.filter(e => e.status === 'on_leave').length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Org chart by department */}
      {employeesByDept.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-slate-400">
            <Network className="w-12 h-12 mx-auto mb-2 opacity-50" />
            <p>Aucun employé</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-6">
          {employeesByDept.map((dept) => (
            <Card key={dept.name}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-purple-500" />
                  {dept.name}
                  <Badge variant="secondary">{dept.employees.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {dept.employees.map((emp) => (
                    <div key={emp.id} className="flex items-start gap-4 p-4 rounded-lg border hover:bg-slate-50">
                      <Avatar className="w-12 h-12">
                        <AvatarFallback className="bg-slate-900 text-white">
                          {emp.first_name?.[0]}{emp.last_name?.[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium truncate">{emp.first_name} {emp.last_name}</h3>
                          <Badge className={statusColors[emp.status] || statusColors.active}>
                            {emp.status === 'active' ? 'Actif' : emp.status === 'on_leave' ? 'Congé' : 'Inactif'}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500">{emp.position}</p>
                        <div className="flex flex-col gap-1 mt-2 text-xs text-slate-400">
                          <span className="flex items-center gap-1 truncate"><Mail className="w-3 h-3" />{emp.email}</span>
                          {emp.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{emp.phone}</span>}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Ajouter un employé</DialogTitle></DialogHeader>
          <form onSubmit={handleCreate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prénom *</Label>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Nom *</Label>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Département *</Label>
                <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} placeholder="IT, RH, etc." />
              </div>
              <div className="space-y-2">
                <Label>Poste *</Label>
                <Input value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Téléphone</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={submitting} className="bg-slate-900 hover:bg-slate-800">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Ajouter'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
